import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { JournalEntry } from "../../types";
import { CreateJournalEntryResponse, UpdateJournalEntryResponse } from "../../types/responses";
import { API_ENDPOINTS, authenticatedFetch } from "../../config/api";

interface JournalState {
  items: JournalEntry[];
  loading: boolean;
  error: string | null;
}

// Async thunk for fetching journal entries
const fetchJournalEntries = createAsyncThunk(
  "journal/fetchJournalEntries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.JOURNAL.GET_ALL);

      if (!response.ok) {
        throw new Error(`Failed to fetch journal entries: ${response.statusText}`);
      }

      const journalEntries: JournalEntry[] = await response.json();
      return journalEntries;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for creating a journal entry
const createJournalEntry = createAsyncThunk(
  "journal/createJournalEntry",
  async (entryData: Omit<JournalEntry, "id" | "userId" | "createdAt" | "updatedAt">, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.JOURNAL.CREATE, {
        method: 'POST',
        body: JSON.stringify(entryData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create journal entry: ${response.statusText}`);
      }

      const result: CreateJournalEntryResponse = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for fetching a single journal entry
const fetchJournalEntryById = createAsyncThunk(
  "journal/fetchJournalEntryById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.JOURNAL.GET_BY_ID(id));

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Journal entry not found");
        }
        throw new Error(`Failed to fetch journal entry: ${response.statusText}`);
      }

      const journalEntry: JournalEntry = await response.json();
      return journalEntry;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for fetching a journal entry by date
const fetchJournalEntryByDate = createAsyncThunk(
  "journal/fetchJournalEntryByDate",
  async (date: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.JOURNAL.GET_BY_RANGE}?startDate=${date}&endDate=${date}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch journal entry: ${response.statusText}`);
      }

      const journalEntries: JournalEntry[] = await response.json();
      return journalEntries.length > 0 ? journalEntries[0] : null;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for updating a journal entry
const updateJournalEntryById = createAsyncThunk(
  "journal/updateJournalEntryById",
  async ({ id, updates }: { id: string; updates: Partial<JournalEntry> }, { rejectWithValue }) => {
    try {
      // Exclude fields that shouldn't be sent to the API
      const { id: _, createdAt, updatedAt, userId, ...apiData } = updates;

      const response = await authenticatedFetch(API_ENDPOINTS.JOURNAL.UPDATE(id), {
        method: 'PUT',
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update journal entry: ${response.statusText}`);
      }

      const result: UpdateJournalEntryResponse = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for deleting a journal entry
const deleteJournalEntryById = createAsyncThunk(
  "journal/deleteJournalEntryById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.JOURNAL.DELETE(id), {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete journal entry: ${response.statusText}`);
      }

      return id;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

const initialState: JournalState = {
  items: [],
  loading: false,
  error: null,
};

const journalSlice = createSlice({
  name: "journal",
  initialState,
  reducers: {
    addJournalEntry: (
      state,
      action: PayloadAction<Omit<JournalEntry, "id" | "createdAt" | "updatedAt">>
    ) => {
      // This reducer is for optimistic updates, but we'll primarily use API responses
    },
    updateJournalEntry: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<JournalEntry> }>
    ) => {
      const { id, updates } = action.payload;
      const entryIndex = state.items.findIndex((entry) => entry.id === id);
      if (entryIndex !== -1) {
        state.items[entryIndex] = {
          ...state.items[entryIndex],
          ...updates,
          updatedAt: new Date().toISOString(),
        };
      }
    },
    deleteJournalEntry: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((entry) => entry.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearJournalEntries: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJournalEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJournalEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchJournalEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createJournalEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJournalEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createJournalEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchJournalEntryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJournalEntryById.fulfilled, (state, action) => {
        state.loading = false;
        // For single entry fetch, we don't add to main items array
        // This is handled separately in the component
      })
      .addCase(fetchJournalEntryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchJournalEntryByDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJournalEntryByDate.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.items.findIndex(j => j.id === action.payload!.id);
          if (index === -1) {
            state.items.push(action.payload);
          }
        }
      })
      .addCase(fetchJournalEntryByDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateJournalEntryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJournalEntryById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(j => j.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateJournalEntryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteJournalEntryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJournalEntryById.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(j => j.id !== action.payload);
      })
      .addCase(deleteJournalEntryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addJournalEntry,
  updateJournalEntry,
  deleteJournalEntry,
  setLoading,
  setError,
  clearJournalEntries
} = journalSlice.actions;
export {
  fetchJournalEntries,
  createJournalEntry,
  fetchJournalEntryById,
  fetchJournalEntryByDate,
  updateJournalEntryById,
  deleteJournalEntryById
};
export default journalSlice.reducer;
